import { useMutation } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import { captureException } from '@sentry/react';
import AuthHeadline from '../Components/AuthHeadline';
import AuthLayout from '../Components/AuthLayout';
import RegistrationForm from '../Components/Forms/RegistrationForm';
import useMessages from 'hooks/useMessages';
import { LOGIN, REGISTER_INVITED_PARTNER } from 'graphql/auth/mutations/auth.mutations';
import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IRegisterPartnerValues, IRegisterValues } from 'FeatureModules/Auth/types/auth.types';
import { StringParam, useQueryParam } from 'use-query-params';

const RegisterInvitedPartner: FC = () => {
  const [hasError, setHasError] = useState(false);
  const { setErrorMessage } = useMessages();
  const navigate = useNavigate();
  const [email] = useQueryParam('email', StringParam);
  const [partnerNameQueryParam] = useQueryParam('company_name', StringParam);
  const [invitedByCompanyId] = useQueryParam('invited_by_company_id', StringParam);
  const [invitedByUserId] = useQueryParam('invited_by_user_id', StringParam);
  const [invitedCompanyId] = useQueryParam('invited_company_id', StringParam);

  const [login] = useMutation(LOGIN, {
    onError: error => {
      captureException(error);
      setErrorMessage('Your automatic login attempt failed. Please try again.');
      navigate('/login');
    },
    onCompleted: () => {
      navigate('/');
    },
    fetchPolicy: 'no-cache',
  });

  const [registerPartner] = useMutation<
    Record<string, unknown>,
    {
      input: IRegisterPartnerValues;
    }
  >(REGISTER_INVITED_PARTNER, {
    onError: error => {
      captureException(error);
      setHasError(true);
    },
    fetchPolicy: 'no-cache',
  });

  // Completes the registration for the partner and then logs
  // them in to the platform.
  const onSubmitPartner = async (values: IRegisterValues) => {
    await registerPartner({
      variables: {
        input: {
          email: values.email,
          password: values.password,
          firstName: values.firstName,
          lastName: values.lastName,
          companyName: values.companyName,
          industry: values.industry,
          termsAccepted: values.termsAccepted,
          invitedByCompanyId: invitedByCompanyId ?? '',
          invitedCompanyId: invitedCompanyId ?? '',
          invitedByUserId: invitedByUserId ?? '',
        },
      },
    });

    login({
      variables: { email: values.email, password: values.password },
    });
  };

  return (
    <AuthLayout
      title="Register"
      isPartnerRegistration
      partnerRegistrationName={partnerNameQueryParam ?? undefined}
    >
      {hasError && (
        <Box mt={12}>
          <Typography variant="h5" color="primary">
            Registration Error
          </Typography>
          <Box mt={2}>
            <Typography color="primary">
              Something went wrong while registering. Please contact your account administrator. It
              could be that the invitation link was expired, in this case you need to get a new
              invitation from your client.
            </Typography>
          </Box>
        </Box>
      )}

      {!hasError && (
        <Box>
          <AuthHeadline text="Welcome to" highlighted="the transparency tool" />
          <Box mt={5}>
            <RegistrationForm
              onSubmit={onSubmitPartner}
              isPartnerRegistration
              email={email ?? ''}
            />
          </Box>
        </Box>
      )}
    </AuthLayout>
  );
};

export default RegisterInvitedPartner;
